import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessageKey } from '@/shared/lib/errors';
import { Button } from '@/shared/ui/Button';
import { MapView } from '@/shared/ui/MapView';
import { TextArea } from '@/shared/ui/TextArea';
import { TextField } from '@/shared/ui/TextField';

import { useCreateMasjid } from '../hooks/use-masjids';

const emptyToNull = (v: string): string | null => (v.trim().length === 0 ? null : v.trim());

/** Parses a coordinate pair; returns undefined if invalid (partial fill). */
function parseCoords(
  lat: string,
  lng: string,
): { latitude: number | null; longitude: number | null } | undefined {
  const l = lat.trim();
  const g = lng.trim();
  if (l === '' && g === '') return { latitude: null, longitude: null };
  const latNum = Number(l);
  const lngNum = Number(g);
  if (l === '' || g === '' || Number.isNaN(latNum) || Number.isNaN(lngNum)) return undefined;
  if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) return undefined;
  return { latitude: latNum, longitude: lngNum };
}

export function CreateMasjidForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateMasjid();
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [errors, setErrors] = useState<{ name?: string; coords?: string }>({});

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    const nextErrors: typeof errors = {};
    if (name.trim().length < 2) nextErrors.name = t('masjid.nameRequired');
    const coords = parseCoords(lat, lng);
    if (coords === undefined) nextErrors.coords = t('masjid.coordinatesInvalid');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || coords === undefined) return;

    create.mutate(
      {
        name: name.trim(),
        city: emptyToNull(city),
        address: emptyToNull(address),
        latitude: coords.latitude,
        longitude: coords.longitude,
      },
      { onSuccess: onCreated },
    );
  }

  return (
    <form className="stack card" onSubmit={handleSubmit} noValidate>
      <TextField
        label={t('masjid.name')}
        placeholder={t('masjid.nameHint')}
        value={name}
        error={errors.name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField label={t('masjid.city')} value={city} onChange={(e) => setCity(e.target.value)} />
      <TextArea
        label={t('masjid.address')}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
      <p className="field__label">{t('masjid.coordinates')}</p>
      <p className="muted">{t('admin.mapPickHint')}</p>
      <MapView
        height={280}
        droppedPin={
          Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)) && lat !== '' && lng !== ''
            ? { lat: Number(lat), lng: Number(lng) }
            : null
        }
        onPinDrop={(newLat, newLng) => {
          setLat(newLat.toFixed(6));
          setLng(newLng.toFixed(6));
        }}
      />
      <div className="row">
        <TextField
          label={t('masjid.latitude')}
          value={lat}
          ltr
          inputMode="decimal"
          onChange={(e) => setLat(e.target.value.replace(/[^0-9.-]/g, ''))}
        />
        <TextField
          label={t('masjid.longitude')}
          value={lng}
          ltr
          inputMode="decimal"
          onChange={(e) => setLng(e.target.value.replace(/[^0-9.-]/g, ''))}
        />
      </div>
      {errors.coords ? (
        <p className="text-danger" role="alert">
          {errors.coords}
        </p>
      ) : null}
      {create.isError ? (
        <p className="text-danger" role="alert">
          {t(errorMessageKey(create.error))}
        </p>
      ) : null}
      <Button type="submit" block loading={create.isPending}>
        {t('masjid.create')}
      </Button>
    </form>
  );
}
